import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  CircularProgress,
  IconButton,
  Divider,
  Chip,
  Avatar,
  Tooltip,
  Collapse,
} from '@mui/material';
import { Send, SmartToy, Person, Undo, LightbulbOutlined } from '@mui/icons-material';
import { useTemplateBuilder } from './TemplateBuilderContext';
import ThinkingAnimation from '../Common/ThinkingAnimation';
import Logo from '../Icons/Logo';
import { useSubjectLanguage } from '@features/user';
import { useEventTracking } from '@services/statsig/hooks';

const ChatInterface: React.FC = () => {
  const subjectLanguage = useSubjectLanguage();
  const { template: templateMetrics } = useEventTracking();
  const {
    chatMessages = [], // Provide fallback empty array
    addUserMessage = () => {}, // Provide fallback no-ops
    updateSectionsAsync = async () => {},
    templateSections = [],
    selectedSectionId = null,
    processingSectionIds = [],
    currentProcessingStage = null,
    currentPollingTaskId = null,
    handleUndoTemplate = async () => {},
    sessionId = null,
  } = useTemplateBuilder();

  const [userInput, setUserInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPromptLibraryOpen, setIsPromptLibraryOpen] = useState(
    !chatMessages.some(msg => msg.role === 'user'),
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to the bottom when messages change or when processing state changes
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, isProcessing, currentPollingTaskId]);

  // Debug log to help troubleshoot chat message updates
  useEffect(() => {
    console.log('Chat messages updated:', chatMessages);
  }, [chatMessages]);

  // Auto-focus the input field on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Handle sending a message
  const handleSendMessage = async () => {
    if (!userInput.trim()) return;

    setIsProcessing(true);
    
    // Track AI message sent
    if (sessionId) {
      templateMetrics.aiMessageSent(sessionId, {
        message_type: 'refinement',
        selected_section_id: selectedSectionId || undefined,
        prompt_source: 'user_typed',
        processing_stage: currentProcessingStage || undefined,
      });
    }

    try {
      // If we have a selected section, update just that one
      if (selectedSectionId) {
        await updateSectionsAsync([selectedSectionId], userInput);
      } else {
        // Otherwise, update all sections
        const allSectionIds = templateSections.map(section => section.module_id);
        await updateSectionsAsync(allSectionIds, userInput);
      }
      
      // Track successful regeneration
      if (sessionId) {
        templateMetrics.aiRegenerationCompleted(sessionId, {
          sections_updated: selectedSectionId ? 1 : templateSections.length,
          success: true,
        });
      }

      // Clear the input
      setUserInput('');
      // Close the prompt library after sending
      setIsPromptLibraryOpen(false);
    } catch (error) {
      console.error('Failed to process message:', error);
      // Track failed regeneration
      if (sessionId) {
        templateMetrics.aiRegenerationCompleted(sessionId, {
          sections_updated: 0,
          success: false,
          error_type: 'processing_failed',
        });
      }
    } finally {
      setIsProcessing(false);

      // After a short delay, focus the input again
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  // Handle selecting a prompt from the library
  const handleSelectPrompt = (prompt: string) => {
    setUserInput(prompt);
    setIsPromptLibraryOpen(false);
    
    // Track prompt library usage
    if (sessionId) {
      templateMetrics.aiMessageSent(sessionId, {
        message_type: 'prompt',
        prompt_source: 'prompt_library',
      });
    }
    
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isAnySectionProcessing) {
        handleSendMessage();
      }
    }
  };

  // Check if any processing is happening
  const isAnySectionProcessing = !!currentPollingTaskId;

  // Determine if we are in a processing state where we should show the thinking animation
  const isThinking = isProcessing || isAnySectionProcessing || currentPollingTaskId !== null;

  // Define thinking words based on the current processing stage
  const getThinkingWords = (): string[] => {
    if (!currentProcessingStage) {
      return ['Thinking...', 'Processing...', 'Working...'];
    }

    // Customize words based on stage
    switch (currentProcessingStage) {
      case 'stage_1_parsing_message':
        return ['Analyzing...', 'Understanding...', 'Processing...'];
      case 'stage_2_identifying_modules':
        return ['Identifying...', 'Analyzing...', 'Determining...'];
      case 'stage_3_planning_changes':
        return ['Planning...', 'Drafting...', 'Organizing...'];
      case 'stage_4_applying_changes':
        return ['Applying...', 'Updating...', 'Adjusting...'];
      case 'stage_5_generating_content':
        return ['Writing...', 'Generating...', 'Creating...'];
      default:
        return ['Thinking...', 'Processing...', 'Working...'];
    }
  };

  // Find the index of the most recent assistant message
  const lastAssistantMessageIndex = chatMessages
    .map((msg, index) => ({ ...msg, index }))
    .filter(msg => msg.role === 'assistant')
    .pop()?.index;

  // Check if current message is the welcome message (first assistant message)
  const isWelcomeMessage = (index: number) => {
    return index === chatMessages.findIndex(msg => msg.role === 'assistant');
  };

  // Dynamic prompts based on template sections
  const samplePrompts = useMemo(() => {
    if (!templateSections || templateSections.length === 0) {
      return [
        'Make the introduction more engaging.',
        'Add a section about key features.',
        'Explain the benefits more clearly.',
      ];
    }

    // Define all prompt templates
    const promptTemplates = [
      'Make {section} more concise.',
      '{section} should have more details from the encounter.',
      '{section} should be in bullet points.',
      'Add another subbullet to {section} about...',
      `Add a direct ${subjectLanguage} quote to {section}.`,
      'The {section} should always be in Spanish.',
      'Add a new section about...',
    ];

    // Get random section names to use in the prompts
    const getRandomSectionName = () => {
      const randomIndex = Math.floor(Math.random() * templateSections.length);
      return templateSections[randomIndex].public_name || 'this section';
    };

    // Create all prompts with random section names inserted
    return promptTemplates.map(template => {
      if (template.includes('{section}')) {
        return template.replace('{section}', getRandomSectionName());
      }
      return template;
    });
  }, [templateSections]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: theme => `1px solid ${theme.palette.divider}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Chat messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          mb: 2,
          pr: 1,
        }}
      >
        {chatMessages.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Send a message to update your template
            </Typography>
          </Box>
        ) : (
          chatMessages.map((message, index) => (
            <Box
              key={message.id || Math.random().toString()}
              sx={{
                display: 'flex',
                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                mb: 2,
              }}
            >
              {message.role === 'assistant' && (
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 32,
                    height: 32,
                    mr: 1,
                  }}
                >
                  <Logo />
                </Avatar>
              )}
              <Box sx={{ maxWidth: '80%' }}>
                <Box sx={{ position: 'relative' }}>
                  <Paper
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: message.role === 'user' ? 'primary.light' : 'background.default',
                      color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                      border: theme =>
                        message.role === 'assistant'
                          ? `1px solid ${theme.palette.divider}`
                          : 'none',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {message.content}
                    </Typography>
                  </Paper>
                  {message.role === 'assistant' &&
                    index === lastAssistantMessageIndex &&
                    !isThinking &&
                    !isWelcomeMessage(index) && (
                      <Tooltip title="Undo last change">
                        <IconButton
                          size="small"
                          onClick={handleUndoTemplate}
                          sx={{
                            position: 'absolute',
                            bottom: -12,
                            right: -12,
                            bgcolor: 'background.paper',
                            border: theme => `1px solid ${theme.palette.divider}`,
                            '&:hover': {
                              bgcolor: 'background.paper',
                            },
                          }}
                        >
                          <Undo fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    ml: 1.5,
                    display: 'block',
                    mt: 0.5,
                    textAlign: message.role === 'user' ? 'right' : 'left',
                  }}
                >
                  {new Date(message.timestamp || Date.now()).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {message.section_id && ' • Section update'}
                </Typography>
              </Box>
              {message.role === 'user' && (
                <Avatar
                  sx={{
                    bgcolor: 'primary.dark',
                    width: 32,
                    height: 32,
                    ml: 1,
                  }}
                >
                  <Person fontSize="small" />
                </Avatar>
              )}
            </Box>
          ))
        )}

        {/* Thinking animation - shown only when processing */}
        {isThinking && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              mb: 2,
            }}
          >
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 32,
                height: 32,
                mr: 1,
              }}
            >
              <Logo />
            </Avatar>
            <Box sx={{ maxWidth: '80%' }}>
              <Paper
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'background.default',
                  border: theme => `1px solid ${theme.palette.divider}`,
                }}
              >
                <ThinkingAnimation words={getThinkingWords()} />
              </Paper>
            </Box>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Prompt Library Suggestions - Animated */}
      <Collapse in={isPromptLibraryOpen} mountOnEnter unmountOnExit>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            p: 1,
            mb: 1,
            borderRadius: 1,
            maxHeight: '30vh',
            overflowY: 'auto',
          }}
        >
          {samplePrompts.map((prompt, index) => (
            <Chip
              key={index}
              label={prompt}
              onClick={() => handleSelectPrompt(prompt)}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      </Collapse>

      {/* Input area */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          pt: 1,
          borderTop: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          size="small"
          placeholder={
            isPromptLibraryOpen
              ? 'Select a prompt or type your own...'
              : "Describe what you'd like to change..."
          }
          value={userInput}
          onChange={e => setUserInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isProcessing}
          multiline
          maxRows={4}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 4,
            },
          }}
        />
        <IconButton
          color="primary"
          onClick={() => setIsPromptLibraryOpen(!isPromptLibraryOpen)}
          disabled={isProcessing || isAnySectionProcessing}
          sx={{ alignSelf: 'flex-end' }}
          title={isPromptLibraryOpen ? 'Hide suggestions' : 'Show suggestions'}
        >
          <LightbulbOutlined />
        </IconButton>
        <IconButton
          color="primary"
          onClick={handleSendMessage}
          disabled={!userInput.trim() || isProcessing || isAnySectionProcessing}
          sx={{ alignSelf: 'flex-end' }}
        >
          {isProcessing ? <CircularProgress size={24} /> : <Send />}
        </IconButton>
      </Box>
    </Paper>
  );
};

export default ChatInterface;
